import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const getLogoUrl = (logoPath) => {
  if (!logoPath) return '';
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) return logoPath;
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}${logoPath}`;
};

const SALES_CHANNELS_LABELS = {
  whatsapp: 'واتساب',
  instagram: 'انستغرام',
  facebook: 'فيسبوك',
  website: 'موقع إلكتروني',
  dont_sell_online: 'لا أبيع أونلاين بعد',
};

const PAIN_POINTS_LABELS = {
  replying_takes_too_much_time: 'الرد على العملاء يأخذ وقتاً طويلاً',
  need_more_sales: 'أحتاج مزيداً من المبيعات',
  dont_post_regularly: 'لا أنشر بانتظام',
  want_automation: 'أريد أتمتة',
  need_better_ads: 'أحتاج إعلانات أفضل',
  managing_orders_messy: 'إدارة الطلبات فوضوية',
  no_team_to_handle_messages: 'لا يوجد فريق للرد على الرسائل',
};

const VOLUME_LABELS = { '0-50': '0 – 50', '50-200': '50 – 200', '200-1000': '200 – 1000', '1000+': '1000+' };

const LeadDetailModal = ({ leadId, onClose, onUpdated }) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ status: 'lead', notes: '', customerId: '' });
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    apiRequest(`/admin/leads/${leadId}`)
      .then((res) => {
        if (res?.success && res.data) {
          setLead(res.data);
          setFormData({
            status: res.data.status || 'lead',
            notes: res.data.notes || '',
            customerId: res.data.customerId ? (res.data.customerId._id || res.data.customerId) : '',
          });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => {
    apiRequest('/admin/customers?limit=500')
      .then((res) => {
        if (res?.success && res.data) setCustomers(res.data);
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    const payload = { ...formData };
    if (!payload.customerId) delete payload.customerId;
    try {
      const data = await apiRequest(`/admin/leads/${leadId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (data?.success) {
        setMessage({ type: 'success', text: 'تم تحديث البيانات بنجاح' });
        setLead(data.data);
        setEditMode(false);
        if (onUpdated) onUpdated(data.data);
      } else {
        setMessage({ type: 'error', text: data?.message || 'حدث خطأ أثناء التحديث' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setSaving(false);
    }
  };

  const displayName = (lead || {}).fullName ?? (lead || {}).storeOwnerName ?? '—';
  const displayBusiness = (lead || {}).businessName ?? (lead || {}).storeName ?? '—';
  const displayPhone = (lead || {}).mobileWhatsApp ?? (lead || {}).phoneNumber ?? '—';
  const isNewSchema = lead && (lead.fullName != null || lead.businessName != null || lead.mobileWhatsApp != null);

  const getStatusBadge = (status) => {
    const map = {
      lead: { label: 'طلب جديد', class: 'badge-info' },
      setup: { label: 'قيد الإعداد', class: 'badge-warning' },
      converted: { label: 'تم التحويل', class: 'badge-success' },
      rejected: { label: 'مرفوض', class: 'badge-danger' },
    };
    const info = map[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${info.class}`}>{info.label}</span>;
  };

  if (!leadId) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-lead" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">تفاصيل الطلب</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="إغلاق">
            ×
          </button>
        </div>

        {loading ? (
          <div className="loading" style={{ padding: '2rem' }}>
            <div className="loading-spinner"></div>
            جاري التحميل...
          </div>
        ) : !lead ? (
          <div className="alert alert-danger">البيانات غير موجودة</div>
        ) : (
          <>
            {message.text && (
              <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                {message.text}
              </div>
            )}

            <div className="modal-body">
              <div className="lead-detail-grid">
                <section>
                  <h3 className="lead-detail-section-title">البيانات الأساسية</h3>
                  <p><strong>الاسم:</strong> {displayName}</p>
                  <p><strong>اسم النشاط:</strong> {displayBusiness}</p>
                  {lead.email ? <p><strong>البريد الإلكتروني:</strong> {lead.email}</p> : null}
                  <p><strong>رقم الجوال / واتساب:</strong> {displayPhone}</p>
                  {isNewSchema && <p><strong>المدينة / الدولة:</strong> {lead.cityCountry || '—'}</p>}
                  <p><strong>المصدر:</strong> {lead.source === 'lead-generation' ? 'طلب أولي' : 'إعداد كامل'}</p>
                  <p><strong>الحالة:</strong> {getStatusBadge(lead.status)}</p>
                </section>

                {isNewSchema && (
                  <>
                    <section>
                      <h3 className="lead-detail-section-title">تفاصيل النشاط</h3>
                      <p><strong>ماذا تبيع؟</strong> {lead.whatDoYouSell || '—'}</p>
                      <p><strong>لديك متجر فعلي؟</strong> {lead.hasPhysicalStore === true ? 'نعم' : lead.hasPhysicalStore === false ? 'لا' : '—'}</p>
                      <p><strong>تبيع أونلاين حالياً؟</strong> {lead.sellOnline === true ? 'نعم' : lead.sellOnline === false ? 'لا' : '—'}</p>
                      <p><strong>لديك متجر إلكتروني؟</strong> {lead.hasEcommerce === true ? 'نعم' : lead.hasEcommerce === false ? 'لا' : '—'}</p>
                      {lead.hasEcommerce && lead.ecommerceUrl && (
                        <p><strong>رابط المتجر:</strong> <a href={lead.ecommerceUrl} target="_blank" rel="noopener noreferrer">{lead.ecommerceUrl}</a></p>
                      )}
                    </section>
                    <section>
                      <h3 className="lead-detail-section-title">قنوات البيع</h3>
                      {lead.salesChannels && lead.salesChannels.length > 0 ? (
                        <ul style={{ margin: 0, paddingRight: '1.25rem' }}>
                          {lead.salesChannels.map((ch) => (
                            <li key={ch}>{SALES_CHANNELS_LABELS[ch] || ch}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>—</p>
                      )}
                    </section>
                    <section>
                      <h3 className="lead-detail-section-title">التحديات</h3>
                      {lead.painPoints && lead.painPoints.length > 0 ? (
                        <ul style={{ margin: 0, paddingRight: '1.25rem' }}>
                          {lead.painPoints.map((p) => (
                            <li key={p}>{PAIN_POINTS_LABELS[p] || p}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>—</p>
                      )}
                    </section>
                    <section>
                      <h3 className="lead-detail-section-title">حجم الطلبات الشهري</h3>
                      <p>{lead.monthlyOrderVolume ? VOLUME_LABELS[lead.monthlyOrderVolume] || lead.monthlyOrderVolume : '—'}</p>
                    </section>
                  </>
                )}

                {!isNewSchema && lead.source === 'initial-setup' && (
                  <section>
                    <h3 className="lead-detail-section-title">معلومات المتجر</h3>
                    <p><strong>هاتف المتجر:</strong> {lead.storePhone || '—'}</p>
                    <p><strong>عنوان المتجر:</strong> {lead.storeAddress || '—'}</p>
                    <p><strong>وصف المتجر:</strong> {lead.storeDescription || '—'}</p>
                    {lead.logo && (
                      <p>
                        <strong>الشعار:</strong>
                        <img src={getLogoUrl(lead.logo)} alt="Logo" style={{ maxWidth: '120px', display: 'block', marginTop: '0.5rem' }} />
                      </p>
                    )}
                  </section>
                )}
              </div>

              <div className="lead-detail-notes">
                <h3 className="lead-detail-section-title">ملاحظات</h3>
                {!editMode ? (
                  <>
                    <p>{lead.notes || 'لا توجد ملاحظات'}</p>
                    {lead.status === 'converted' && lead.customerId && (
                      <p style={{ marginTop: '0.75rem' }}>
                        <strong>العميل المرتبط:</strong>{' '}
                        {lead.customerId.name || lead.customerId.email || lead.customerId._id}
                      </p>
                    )}
                  </>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label className="form-label">الحالة</label>
                      <select name="status" className="form-control" value={formData.status} onChange={handleChange}>
                        <option value="lead">طلب جديد</option>
                        <option value="setup">قيد الإعداد</option>
                        <option value="converted">تم التحويل</option>
                        <option value="rejected">مرفوض</option>
                      </select>
                    </div>
                    {formData.status === 'converted' && (
                      <div className="form-group">
                        <label className="form-label">اختر العميل</label>
                        <select
                          name="customerId"
                          className="form-control"
                          value={formData.customerId}
                          onChange={handleChange}
                        >
                          <option value="">— اختر عميلاً —</option>
                          {customers.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name || c.email}{c.name && c.email ? ` (${c.email})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">ملاحظات</label>
                      <textarea name="notes" className="form-control" value={formData.notes} onChange={handleChange} rows={4} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? 'جاري الحفظ...' : 'حفظ'}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setEditMode(false)}>
                        إلغاء
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {!editMode && (
                <div style={{ marginTop: '1rem' }}>
                  <button type="button" className="btn btn-primary" onClick={() => setEditMode(true)}>
                    تعديل الحالة والملاحظات
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadDetailModal;
